import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Container, Table, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { Base_URL } from "../url";
import { useReactToPrint } from "react-to-print";

const number = (v) => (v ?? 0);
const fmt = (v) =>
  Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

const PurchaseOrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // how many copies to print
  const [prints, setPrints] = useState(1);

  // This ref will be used by react-to-print
  const printRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(
          `${Base_URL}/PurchaseOrder/purchaseOrderItems/id`,
          { params: { id } }
        );
        if (!active) return;
        setRows(Array.isArray(res.data.items) ? res.data.items : []);
        console.log(res.data.items);
      } catch (e) {
        toast.error("Failed to fetch PO items");
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);
  
  const totals = useMemo(() => {
    const sum = (key) => rows.reduce((acc, r) => acc + number(r[key]), 0);
    return {
      sgstAmount: sum("sgstAmount"),
      cgstAmount: sum("cgstAmount"),
      igstAmount: sum("igstAmount"),
      extendedTotal: rows.reduce(
        (acc, r) => acc + number(r.quantity) * number(r.totalAmount),
        0
      ),
      subtotal: rows.reduce(
        (acc, r) => acc + number(r.quantity) * number(r.price),
        0
      ),
    };
  }, [rows]);

  // ------------ PRINT (react-to-print v3) ------------
  const handlePrint = useReactToPrint({
    contentRef: printRef, // ✅ v3 API, points to a DOM node
    documentTitle: `PO_${id}`,
  });

  // Reusable table block so we can render duplicates for multi-print
  const TableBlock = () => (
    <div
      style={{
        // on-screen container styles (will be reset in print)
        maxHeight: "70vh",
        overflowY: "auto",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        position: "relative",
      }}
      className="custom-scrollbar"
    >
      <Table bordered hover className="data-table mb-0">
        <thead
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#f8f9fa",
            zIndex: 3,
          }}
        >
          <tr>
            <th>Description</th>
            <th>HSN</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Price</th>
            <th>SGST Amt</th>
            <th>CGST Amt</th>
            <th>IGST Amt</th>
            <th>Unit Total (Incl.)</th>
            <th>Line Total (Incl.)</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => {
            const lineTotalIncl = number(r.quantity) * number(r.totalAmount);
            return (
              <tr key={i}>
                <td>{r.description}</td>
                <td>{r.hsnId}</td>
                <td>{fmt(r.quantity)}</td>
                <td>{r.unit}</td>
                <td>{fmt(r.price)}</td>
                <td>{fmt(r.sgstAmount)}</td>
                <td>{fmt(r.cgstAmount)}</td>
                <td>{fmt(r.igstAmount)}</td>
                <td>{fmt(r.totalAmount)}</td>
                <td>{fmt(lineTotalIncl)}</td>
              </tr>
            );
          })}
        </tbody>

        <tfoot
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#eaf2ff",
            zIndex: 3,
            borderTop: "2px solid #c8d8ff",
          }}
        >
          <tr>
            <th colSpan={5} style={{ textAlign: "right" }}>
              Totals:
            </th>
            <th>{fmt(totals.sgstAmount)}</th>
            <th>{fmt(totals.cgstAmount)}</th>
            <th>{fmt(totals.igstAmount)}</th>
            <th />
            <th>{fmt(totals.extendedTotal)}</th>
          </tr>
          <tr>
            <th colSpan={9} style={{ textAlign: "right" }}>
              Subtotal:
            </th>
            <th>{fmt(totals.subtotal)}</th>
          </tr>
        </tfoot>
      </Table>
    </div>
  );

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        <div className="d-flex justify-content-between align-items-center mb-3 no-print">
          <h2 className="list-title">PO #{id} — Items</h2>
          <div className="d-flex gap-2 align-items-center">
            <div className="d-flex align-items-center me-2">
              <label className="me-2 fw-semibold">Number of Prints</label>
              <input
                type="number"
                min={1}
                value={prints}
                onChange={(e) => setPrints(Math.max(1, Number(e.target.value) || 1))}
                style={{ width: 80 }}
              />
            </div>
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              Back
            </Button>
            <Button variant="primary" onClick={handlePrint}>
              Print
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span>Loading items…</span>
          </div>
        ) : rows.length === 0 ? (
          <p className="no-data">No items found for this purchase order.</p>
        ) : (
          <>
            {/* On-screen single view */}
            <div className="no-print">
              <TableBlock />
            </div>

            {/* Print-only container with duplicates */}
            <div ref={printRef} className="d-none d-print-block">
              {Array.from({ length: prints }).map((_, idx) => (
                <div className="po-print" key={idx}>
                  <h2 style={{ textAlign: "center", marginBottom: 16 }}>
                    Purchase Order #{id}
                  </h2>
                  <TableBlock />
                </div>
              ))}
            </div>
          </>
        )}
      </Container>

      {/* Styles (screen + print) */}
      <style>
        {`
        .custom-scrollbar::-webkit-scrollbar {
          width: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #999;
          border-radius: 10px;
          border: 2px solid #f1f1f1;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        /* Print helpers */
        @media print {
          .no-print { display: none !important; }

          /* Ensure all copies start on new pages */
          .po-print { page-break-after: always; }
          .po-print:last-child { page-break-after: auto; }

          /* Remove sticky & scrolling in print */
          .custom-scrollbar {
            overflow: visible !important;
            max-height: none !important;
            border: none !important;
          }
          thead, tfoot {
            position: static !important;
            box-shadow: none !important;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        `}
      </style>
    </div>
  );
};

export default PurchaseOrderDetails;
